import {
  type ConversationDto,
  type ConversationParticipant,
  type GetMessagesResponse,
  type MessageAttachment
} from '@shared/all/domains/messages';
import { type SocketUserTag } from '@shared/all/domains/socket';

import { type ReducerStore } from '../helpers/createReducerStore';
import { forgetConversationRoom } from '../socket/conversationRooms';
import {
  type ConversationAction,
  type ConversationsState,
  type LocalMessage
} from './context';

export type ConversationsStore = ReducerStore<
  ConversationsState,
  ConversationAction
>;

// How long a typing indicator stays up without another typing event.
const TYPING_TIMEOUT_MS = 5000;

/**
 * Everything that changes the conversations store. Built once per store and
 * handed out by useConversationActions, so a component that only acts on
 * conversations subscribes to nothing. Anything that needs the current state
 * reads it from the store when it runs.
 */
export const createConversationActions = (store: ConversationsStore) => {
  const { dispatch } = store;
  // A live message (from a socket) and an optimistic temp carry fewer fields
  // than a fetched message: no reactions, reply count or edit flags yet. The
  // reducer and the transcript treat the missing ones as empty, as they did
  // when this dispatch was untyped, so these three actions skip the payload
  // check rather than invent values.
  const dispatchPartialMessage = dispatch as (action: {
    type: 'UPDATE_LAST_MESSAGE' | 'PREPEND_MESSAGE';
    payload: unknown;
  }) => void;
  // The typing indicators' timers, keyed `${conversationId}-${userId}`. Kept
  // here, beside the store, not in the reducer state: a timer is not state to
  // render, and a Map in the initial state was one shared object that outlived
  // a reset.
  const typingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  const clearTypingTimeout = (key: string) => {
    const timeout = typingTimeouts.get(key);
    if (timeout) clearTimeout(timeout);
    typingTimeouts.delete(key);
  };

  const removeConversation = (conversationId: string) => {
    // Gone from the list: its socket room is not asked for again.
    forgetConversationRoom(conversationId);
    dispatch({ type: 'REMOVE_CONVERSATION', payload: conversationId });
  };

  return {
    /** The conversations as they are now, for event handlers. */
    getConversations: () => store.getState().conversations,

    toggleConversationList: () => {
      dispatch({ type: 'TOGGLE_CONVERSATION_LIST' });
    },

    setConversations: (conversations: ConversationDto[]) => {
      dispatch({
        type: 'SET_CONVERSATIONS',
        payload: conversations.map((conv) => ({ ...conv, usersTyping: [] }))
      });
    },

    openConversation: (conversationId: string) => {
      dispatch({
        type: 'UPDATE_CONVERSATION_STATE',
        payload: { conversationId, state: 'OPEN' }
      });
      // Mark as read immediately when opening
      dispatch({ type: 'UPDATE_READ_RECEIPT', payload: conversationId });
    },

    rejectDisputeConversation: (conversationId: string) => {
      dispatch({ type: 'REJECT_DISPUTE', payload: conversationId });
    },

    exitConversation: (conversationId: string) => {
      forgetConversationRoom(conversationId);
      dispatch({ type: 'REMOVE_CONVERSATION', payload: conversationId });
    },

    closeConversation: (conversationId: string) => {
      dispatch({
        type: 'UPDATE_CONVERSATION_STATE',
        payload: { conversationId, state: 'CLOSED' }
      });
    },

    minimizeConversation: (conversationId: string) => {
      dispatch({
        type: 'UPDATE_CONVERSATION_STATE',
        payload: { conversationId, state: 'MINIMIZED' }
      });
    },

    resetConversations: () => {
      typingTimeouts.forEach((timeout) => clearTimeout(timeout));
      typingTimeouts.clear();
      dispatch({ type: 'RESET_CONVERSATIONS' });
    },

    setOpenConversations: (
      openConversations: {
        id: string;
        conversationState: 'OPEN' | 'MINIMIZED';
      }[]
    ) => {
      dispatch({ type: 'SET_OPEN_CONVERSATIONS', payload: openConversations });
    },

    updateActivityStatus: (
      userId: number,
      lastActive: Date,
      isActive: boolean,
      convId: string
    ) => {
      dispatch({
        type: 'UPDATE_ACTIVITY_STATUS',
        payload: { userId, lastActive, isActive, conversationId: convId }
      });
    },

    /**
     * Accepts conversations from anywhere they are produced — the `get` list, a
     * `start`/`joinChannel` mutation result, or a `newConversation` socket
     * payload. The last two describe something brand new, so unread/mute are
     * defaulted here rather than made every caller's problem.
     */
    addConversations: (
      conversations: Array<
        Omit<ConversationDto, 'unreadCount' | 'muted'> & {
          unreadCount?: number;
          muted?: boolean;
        }
      >
    ) => {
      dispatch({
        type: 'ADD_CONVERSATIONS',
        payload: conversations.map((conv) => ({
          ...conv,
          unreadCount: conv.unreadCount ?? 0,
          muted: conv.muted ?? false,
          usersTyping: []
        }))
      });
    },

    updateConversationName: (conversationId: string, newName: string) => {
      dispatch({
        type: 'UPDATE_CONVERSATION_NAME',
        payload: { conversationId, name: newName }
      });
    },

    setUserTyping: (conversationId: string, userId: number) => {
      if (!conversationId || !userId) {
        return;
      }

      const timeoutKey = `${conversationId}-${userId}`;
      clearTypingTimeout(timeoutKey);

      // Update the typing users in the conversation
      dispatch({
        type: 'SET_TYPING',
        payload: {
          conversationId,
          userId
        }
      });

      typingTimeouts.set(
        timeoutKey,
        setTimeout(() => {
          typingTimeouts.delete(timeoutKey);
          dispatch({
            type: 'REMOVE_TYPING',
            payload: {
              conversationId,
              userId
            }
          });
        }, TYPING_TIMEOUT_MS)
      );
    },

    onNewMessage: (
      sender: ConversationParticipant,
      convId: string,
      message: string,
      messageId: number,
      createdAt: Date,
      attachments?: MessageAttachment[],
      fromSelf = false
    ) => {
      // Update last message in conversation list
      dispatchPartialMessage({
        type: 'UPDATE_LAST_MESSAGE',
        payload: {
          conversationId: convId,
          lastMessage: { id: messageId, content: message, createdAt, sender },
          fromSelf
        }
      });

      // Remove typing indicator for sender
      dispatch({
        type: 'REMOVE_TYPING',
        payload: { conversationId: convId, userId: sender.id }
      });
      clearTypingTimeout(`${convId}-${sender.id}`);

      // Prepend message to context messages store
      dispatchPartialMessage({
        type: 'PREPEND_MESSAGE',
        payload: {
          conversationId: convId,
          message: {
            id: messageId,
            content: message,
            sender,
            createdAt,
            attachments
          }
        }
      });
    },

    updateReadReceipt: (conversationId: string) => {
      dispatch({ type: 'UPDATE_READ_RECEIPT', payload: conversationId });
    },

    removeConversation,

    handleMemberRemoved: (
      conversationId: string,
      memberId: number,
      currentUserId: number
    ) => {
      dispatch({
        type: 'REMOVE_PARTICIPANT',
        payload: { conversationId, participantId: memberId }
      });

      if (memberId === currentUserId) {
        removeConversation(conversationId);
      }
    },

    // ============= Message Methods =============

    /**
     * Insert an optimistic temp message immediately (chat send UX). The temp
     * has a negative id and stays put until the socket echo replaces it in
     * place — callers must not remove it themselves, or the echo arrives with
     * no slot to land in and gets prepended out of order. Server-only fields
     * (acknowledged*, attachments) are filled with sensible defaults so callers
     * only pass what's known locally.
     */
    optimisticPrependMessage: (
      conversationId: string,
      message: {
        id: number;
        content: string;
        sender: {
          id: number;
          name: string | null;
          username: string;
          avatarKey: string | null;
          robohash: string | null;
          tag: SocketUserTag;
        };
        createdAt: Date;
        pendingPreviewUris?: Array<{
          uri: string;
          isVideo?: boolean;
          name?: string;
        }>;
      }
    ) => {
      dispatchPartialMessage({
        type: 'PREPEND_MESSAGE',
        payload: {
          conversationId,
          message: {
            ...message,
            acknowledged: false,
            acknowledgedBy: null,
            attachments: [],
            pending: true
          }
        }
      });
    },

    /**
     * Apply an edit to a message (content + the edited flag).
     */
    updateMessage: (
      conversationId: string,
      messageId: number,
      content: string
    ) => {
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { conversationId, messageId, content, edited: true }
      });
    },

    /**
     * Merge a partial update into one message — reaction tallies and thread
     * reply counts arrive over sockets and only touch a field or two.
     */
    patchMessage: (
      conversationId: string,
      messageId: number,
      patch: Partial<LocalMessage>
    ) => {
      dispatch({
        type: 'PATCH_MESSAGE',
        payload: { conversationId, messageId, patch }
      });
    },

    /**
     * Tombstone a message deleted for everyone, locally and in the list preview.
     */
    markMessageDeleted: (conversationId: string, messageId: number) => {
      dispatch({
        type: 'DELETE_MESSAGE',
        payload: { conversationId, messageId }
      });
    },

    /**
     * Show a message again after its sender restored it, locally and in the list
     * preview.
     */
    markMessageRestored: (
      conversationId: string,
      messageId: number,
      content: string,
      attachments: MessageAttachment[]
    ) => {
      dispatch({
        type: 'RESTORE_MESSAGE',
        payload: { conversationId, messageId, content, attachments }
      });
    },

    /**
     * Mark a temp optimistic message as failed so the UI can show retry.
     */
    markMessageFailed: (conversationId: string, messageId: number) => {
      dispatch({
        type: 'MARK_MESSAGE_FAILED',
        payload: { conversationId, messageId }
      });
    },

    /**
     * Set messages for a conversation (initial load). `hasNewerPage` marks the
     * page as a window around an older message rather than the live end.
     */
    setMessages: (
      conversationId: string,
      messages: GetMessagesResponse['messages'],
      hasNextPage: boolean,
      cursor: number | undefined,
      hasNewerPage = false
    ) => {
      dispatch({
        type: 'SET_MESSAGES',
        payload: { conversationId, messages, hasNextPage, cursor, hasNewerPage }
      });
    },

    /**
     * Add the page that follows a detached window, toward the live end.
     */
    addNewerMessages: (
      conversationId: string,
      messages: GetMessagesResponse['messages'],
      hasNewerPage: boolean,
      afterId: number
    ) => {
      dispatch({
        type: 'ADD_NEWER_MESSAGES',
        payload: { conversationId, messages, hasNewerPage, afterId }
      });
    },

    /**
     * Add more messages to a conversation (pagination)
     */
    addMessages: (
      conversationId: string,
      messages: GetMessagesResponse['messages'],
      hasNextPage: boolean,
      cursor: number | undefined
    ) => {
      dispatch({
        type: 'ADD_MESSAGES',
        payload: { conversationId, messages, hasNextPage, cursor }
      });
    },

    /**
     * Set loading state for messages
     */
    setMessagesLoading: (conversationId: string, isLoading: boolean) => {
      dispatch({
        type: 'SET_MESSAGES_LOADING',
        payload: { conversationId, isLoading }
      });
    },

    /**
     * Set error state for messages
     */
    setMessagesError: (conversationId: string, error: Error | null) => {
      dispatch({
        type: 'SET_MESSAGES_ERROR',
        payload: { conversationId, error }
      });
    },

    /**
     * Clear messages for a conversation
     */
    clearMessages: (conversationId: string) => {
      dispatch({ type: 'CLEAR_MESSAGES', payload: conversationId });
    }
  };
};

export type ConversationActions = ReturnType<typeof createConversationActions>;
